import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkle } from "lucide-react";
import { resolveImage } from "../lib/api";

const HighlightedProjects = ({ projects = [] }) => {
  const featured = projects
    .filter((p) => p.featured)
    .sort((a, b) => (a.featured_order ?? 999) - (b.featured_order ?? 999))
    .slice(0, 3);

  if (featured.length === 0) return null;

  const Card = ({ p, className = "", style }) => (
    <Link
      to={`/work/${p.slug || p.id}`}
      data-testid={`highlight-card-${p.slug || p.id}`}
      className={`group relative block rounded-[22px] overflow-hidden border border-transparent aspect-[4/5] transition-all duration-500 hover:-translate-y-2 hover:border-[color:var(--violet)] hover:shadow-[0_18px_40px_-10px_rgba(139,123,232,0.55)] ${className}`}
      style={{ backgroundColor: p.color || "#E1D8FF", ...(style || {}) }}
    >
      {p.cover && (
        <img
          src={resolveImage(p.cover)}
          alt={p.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
      <div className="absolute inset-0 rounded-[22px] ring-0 group-hover:ring-2 ring-[color:var(--violet)]/70 transition-all duration-300 pointer-events-none" />
      {p.tag && (
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/95 text-neutral-900 text-[10px] font-medium uppercase tracking-wider">
          {p.tag}
        </div>
      )}
      <span className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/95 text-neutral-900 grid place-items-center transition-transform duration-300 group-hover:rotate-45 group-hover:bg-[color:var(--violet)] group-hover:text-white">
        <ArrowUpRight className="w-4 h-4" />
      </span>
      <div className="absolute bottom-4 left-4 right-4 text-white">
        {p.client && (
          <div className="text-[10px] uppercase tracking-wider text-white/85 truncate">
            {p.client}
          </div>
        )}
        <h3 className="mt-1 font-display italic text-lg md:text-xl font-semibold leading-snug line-clamp-2">
          {p.title}
        </h3>
      </div>
    </Link>
  );

  return (
    <section id="highlighted" className="relative py-16 md:py-24 bg-[color:var(--bg-2)]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[color:var(--muted)]">
            <Sparkle className="w-3.5 h-3.5 text-[color:var(--violet)] fill-current" />
            Highlighted
          </div>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold leading-[1.05]">
            Signature <em className="italic text-[color:var(--violet)]">projects</em>.
          </h2>
        </div>

        {/* Desktop: centered flex wrap */}
        <div
          data-testid="highlighted-projects-desktop"
          className="hidden md:flex flex-wrap justify-center gap-5 md:gap-6"
        >
          {featured.map((p) => (
            <Card key={p.id} p={p} className="w-full max-w-[320px]" />
          ))}
        </div>

        {/* Mobile: horizontal snap carousel with peek of adjacent cards */}
        <div className="md:hidden -mx-5">
          <div
            data-testid="highlighted-projects-mobile"
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar scroll-smooth px-[14%] pb-3"
            style={{ scrollPaddingInline: "14%" }}
          >
            {featured.map((p, i) => (
              <div
                key={p.id}
                className="snap-center shrink-0 w-[72vw]"
              >
                <Card p={p} />
                <div className="mt-2 flex justify-center gap-1">
                  {featured.map((_, j) => (
                    <span
                      key={j}
                      aria-hidden
                      className={`h-1.5 rounded-full transition-all ${
                        j === i
                          ? "w-5 bg-[color:var(--violet)]"
                          : "w-1.5 bg-[color:var(--card-border)]"
                      }`}
                      style={{ visibility: i === 0 ? "visible" : "hidden" }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-1 text-center text-xs text-[color:var(--muted)]">
            Swipe &larr; &rarr; to browse
          </div>
        </div>
      </div>
    </section>
  );
};

export default HighlightedProjects;
